// src/components/Customer/CustomerHome.js
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Bell, Star, LogOut, Plus, List, X, LocateFixed } from "lucide-react";

import { Button } from "../ui/Button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import useCategories from "../../hooks/useCategories";
import { useAuth } from "../../contexts/AuthContext";

import MapPicker from "../maps/MapPicker";
import useUserGeolocation from "../../hooks/useUserGeolocation";
import { loadGoogleMaps } from "../../lib/googleMapsLoader";
import { reverseGeocode } from "../../lib/geocoding";

import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  getDocs,
  documentId,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../../firebase";

const DEFAULT_RIYADH = { lat: 24.7136, lng: 46.6753 };

const SAUDI_CITIES = [
  "Abha",
  "Al-Ahsa",
  "Al Bahah",
  "Al-Kharj",
  "Al-Qassim",
  "Arar",
  "Buraydah",
  "Dammam",
  "Dhahran",
  "Hail",
  "Hafar Al-Batin",
  "Jazan",
  "Jeddah",
  "Jubail",
  "Khamis Mushait",
  "Khobar",
  "Mecca",
  "Medina",
  "Najran",
  "Qatif",
  "Riyadh",
  "Sakakah",
  "Tabuk",
  "Taif",
  "Yanbu",
];

function safeString(v, fallback = "") {
  return typeof v === "string" ? v : fallback;
}
function safeBool(v) {
  return !!v;
}
function safeNumber(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
function isValidLatLng(p) {
  const lat = Number(p?.lat);
  const lng = Number(p?.lng);
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}
function latLngText(loc) {
  if (!isValidLatLng(loc)) return "";
  return `Lat: ${Number(loc.lat).toFixed(6)} | Lng: ${Number(loc.lng).toFixed(6)}`;
}

// -----------------------------
// Geo helpers
// -----------------------------
function toRad(d) {
  return (Number(d) * Math.PI) / 180;
}
function haversineKm(a, b) {
  if (!isValidLatLng(a) || !isValidLatLng(b)) return Infinity;
  const R = 6371;
  const dLat = toRad(Number(b.lat) - Number(a.lat));
  const dLng = toRad(Number(b.lng) - Number(a.lng));
  const lat1 = toRad(Number(a.lat));
  const lat2 = toRad(Number(b.lat));

  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  return R * c;
}

function normalizeAddress(raw) {
  const a = raw && typeof raw === "object" ? raw : {};
  const loc = a.location && typeof a.location === "object" ? a.location : {};
  const lat = safeNumber(loc.lat, null);
  const lng = safeNumber(loc.lng, null);

  return {
    id: a.id,
    type: safeString(a.type, "Other"),
    addressText: safeString(a.addressText, ""),
    phone: safeString(a.phone, ""),
    buildingName: safeString(a.buildingName, ""),
    buildingNo: safeString(a.buildingNo, ""),
    floorNo: safeString(a.floorNo, ""),
    aptNo: safeString(a.aptNo, ""),
    instructions: safeString(a.instructions, ""),
    location: { lat, lng },
    isDefault: safeBool(a.isDefault),
    createdAt: a.createdAt ?? null,
    updatedAt: a.updatedAt ?? null,
  };
}

function normalizeTasker(raw) {
  const t = raw && typeof raw === "object" ? raw : {};
  const loc = t.location && typeof t.location === "object" ? t.location : {};
  const lat = safeNumber(loc.lat, null);
  const lng = safeNumber(loc.lng, null);

  return {
    id: safeString(t.id, ""),
    name: safeString(t.name, ""),
    email: safeString(t.email, ""),
    phone: safeString(t.phone, ""),
    country: safeString(t.country, "Saudi Arabia"),
    city: safeString(t.city, ""),
    bio: safeString(t.bio, ""),
    imageUrl: safeString(t.imageUrl, ""),
    availability: safeString(t.availability, ""),
    responseTime: safeString(t.responseTime, safeString(t["response Time"], "")),
    priceRange: t.priceRange && typeof t.priceRange === "object" ? t.priceRange : null,
    categories: Array.isArray(t.categories) ? t.categories : [],
    subcategories: Array.isArray(t.subcategories) ? t.subcategories : [],
    addressText: safeString(t.addressText, ""),
    location: { lat, lng },
    serviceRadiusKm: safeNumber(t.serviceRadiusKm, null),
    ratingSummary:
      t.ratingSummary && typeof t.ratingSummary === "object"
        ? t.ratingSummary
        : {
            avg: safeNumber(t?.ratingSummary?.avg, 0) || 0,
            count: safeNumber(t?.ratingSummary?.count, 0) || 0,
          },
    createdAt: t.createdAt ?? null,
    updatedAt: t.updatedAt ?? null,
  };
}

export default function CustomerHome({ onLogout }) {
  const navigate = useNavigate();
  const { profile, logout } = useAuth();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();

  const customerUid = profile?.uid || auth?.currentUser?.uid || null;

  // Customer doc (explicitly from Firestore)
  const [customerDoc, setCustomerDoc] = useState(null);

  const customerName = safeString(profile?.roleProfile?.name, safeString(customerDoc?.name, "Unnamed Customer"));
  const customerPhone = safeString(profile?.roleProfile?.phone, safeString(customerDoc?.phone, ""));
  const customerRating = Number(profile?.roleProfile?.rating || 0) || 0;
  const requestedServices = Number(profile?.roleProfile?.requestedServices || 0) || 0;
  const completedJobs = Number(profile?.roleProfile?.completedJobs || 0) || 0;

  // ---------------------------
  // ✅ City Journey (stable city selection)
  // ---------------------------
  const [selectedCity, setSelectedCity] = useState(() => {
    const fromLs = safeString(localStorage.getItem("customerCity"), "").trim();
    return fromLs || "";
  });
  const [showCityModal, setShowCityModal] = useState(false);
  const [savingCity, setSavingCity] = useState(false);

  // Saved addresses
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState("");

  // UI (List only)
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Add Address Modal
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [geoStatusNote, setGeoStatusNote] = useState("");

  // Geolocation: DO NOT auto-run on modal open
  const [geoEnabled, setGeoEnabled] = useState(false);
  const geo = useUserGeolocation({ enabled: showAddAddressModal && geoEnabled });

  // Reverse geocode state
  const [reverseLoading, setReverseLoading] = useState(false);
  const [reverseError, setReverseError] = useState("");
  const revReqIdRef = useRef(0);
  const revDebounceRef = useRef(null);

  // Only reverse-geocode after user interacts (move pin OR use my location)
  const [locTouched, setLocTouched] = useState(false);

  const [newAddress, setNewAddress] = useState({
    type: "Home",
    customLabel: "",
    addressText: "",
    phone: "",
    buildingName: "",
    buildingNo: "",
    floorNo: "",
    aptNo: "",
    instructions: "",
    location: DEFAULT_RIYADH,
    isDefault: false,
  });

  // ---------------------------
  // Read customer profile from Firestore
  // ---------------------------
  useEffect(() => {
    if (!customerUid) {
      setCustomerDoc(null);
      return;
    }

    const ref = doc(db, "customers", customerUid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCustomerDoc(null);
          return;
        }
        setCustomerDoc({ id: snap.id, ...snap.data() });
      },
      (err) => {
        console.error("customers/{uid} snapshot error:", err);
        setCustomerDoc(null);
      }
    );

    return () => unsub();
  }, [customerUid]);

  // Seed selectedCity from Firestore/profile if not already set
  useEffect(() => {
    const fromProfile = safeString(profile?.roleProfile?.city, "").trim();
    const fromDoc = safeString(customerDoc?.city, "").trim();

    setSelectedCity((prev) => {
      if (prev) return prev;
      const v = fromDoc || fromProfile || safeString(localStorage.getItem("customerCity"), "").trim();
      return v || "";
    });
  }, [customerDoc?.city, profile?.roleProfile?.city]);

  // If no city selected, force modal
  useEffect(() => {
    if (!customerUid) return;
    if (selectedCity) return;
    setShowCityModal(true);
  }, [customerUid, selectedCity]);

  const persistCity = useCallback(
    async (city) => {
      const v = safeString(city, "").trim();
      if (!v) return;

      localStorage.setItem("customerCity", v);
      setSelectedCity(v);

      if (!customerUid) return;

      setSavingCity(true);
      try {
        await updateDoc(doc(db, "customers", customerUid), {
          city: v,
          updatedAt: serverTimestamp(),
        });
      } catch (e) {
        try {
          await setDoc(
            doc(db, "customers", customerUid),
            { city: v, updatedAt: serverTimestamp() },
            { merge: true }
          );
        } catch (err2) {
          console.error("Persist city error:", err2);
        }
      } finally {
        setSavingCity(false);
      }
    },
    [customerUid]
  );

  // ---------------------------
  // Read savedAddresses from Firestore
  // ---------------------------
  useEffect(() => {
    if (!customerUid) {
      setAddresses([]);
      setSelectedAddressId("");
      setAddressesLoading(false);
      return;
    }

    setAddressesLoading(true);

    const colRef = collection(db, "customers", customerUid, "savedAddresses");
    const q = query(colRef, orderBy("createdAt", "desc"));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => normalizeAddress({ id: d.id, ...d.data() }));
        setAddresses(list);

        const defaultOne = list.find((x) => x.isDefault);
        const firstOne = list[0];

        setSelectedAddressId((prev) => {
          if (prev && list.some((x) => x.id === prev)) return prev;
          if (defaultOne?.id) return defaultOne.id;
          if (firstOne?.id) return firstOne.id;
          return "";
        });

        setAddressesLoading(false);
      },
      (err) => {
        console.error("savedAddresses snapshot error:", err);
        setAddresses([]);
        setSelectedAddressId("");
        setAddressesLoading(false);
      }
    );

    return () => unsub();
  }, [customerUid]);

  const selectedAddress = useMemo(() => {
    return addresses.find((a) => a.id === selectedAddressId) || null;
  }, [addresses, selectedAddressId]);

  // Customer “center” location used for radius filtering (from SELECTED address)
  const customerCenter = useMemo(() => {
    const a = selectedAddress;
    if (isValidLatLng(a?.location)) return { lat: a.location.lat, lng: a.location.lng };
    return null;
  }, [selectedAddress]);

  // ---------------------------
  // Open add-address modal => initialize fields
  // ---------------------------
  useEffect(() => {
    if (!showAddAddressModal) return;

    setGeoEnabled(false);
    setLocTouched(false);

    setReverseError("");
    setGeoStatusNote("");
    setSavingAddress(false);
    setReverseLoading(false);

    if (revDebounceRef.current) clearTimeout(revDebounceRef.current);
    revReqIdRef.current += 1;

    const seedLoc = isValidLatLng(selectedAddress?.location)
      ? { lat: selectedAddress.location.lat, lng: selectedAddress.location.lng }
      : DEFAULT_RIYADH;

    setNewAddress({
      type: "Home",
      customLabel: "",
      addressText: "",
      phone: customerPhone || "",
      buildingName: "",
      buildingNo: "",
      floorNo: "",
      aptNo: "",
      instructions: "",
      location: seedLoc,
      isDefault: addresses.length === 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddAddressModal]);

  // Update geo status note
  useEffect(() => {
    if (!showAddAddressModal) return;

    if (!geoEnabled) {
      setGeoStatusNote("");
      return;
    }

    if (geo?.loading) setGeoStatusNote("Detecting your location...");
    else if (geo?.error) setGeoStatusNote(geo.error);
    else setGeoStatusNote("");
  }, [showAddAddressModal, geoEnabled, geo?.loading, geo?.error]);

  // When geo coords arrive (after button), set location + touch
  useEffect(() => {
    if (!showAddAddressModal) return;
    if (!geoEnabled) return;

    if (isValidLatLng(geo?.coords)) {
      setLocTouched(true);
      setNewAddress((prev) => ({
        ...prev,
        location: { lat: geo.coords.lat, lng: geo.coords.lng },
      }));
    }
  }, [showAddAddressModal, geoEnabled, geo?.coords]);

  // Reverse geocode ONLY after touch
  useEffect(() => {
    if (!showAddAddressModal) return;
    if (!locTouched) return;
    if (!isValidLatLng(newAddress.location)) return;

    const reqId = ++revReqIdRef.current;

    if (revDebounceRef.current) clearTimeout(revDebounceRef.current);

    revDebounceRef.current = setTimeout(() => {
      let cancelled = false;

      (async () => {
        try {
          setReverseError("");
          setReverseLoading(true);

          await loadGoogleMaps();

          const res = await reverseGeocode({
            lat: Number(newAddress.location.lat),
            lng: Number(newAddress.location.lng),
          });

          if (cancelled) return;
          if (reqId !== revReqIdRef.current) return;

          const formatted = safeString(res?.formattedAddress, "").trim();

          setNewAddress((prev) => ({
            ...prev,
            addressText: formatted || prev.addressText || "",
          }));
        } catch (e) {
          if (cancelled) return;
          if (reqId !== revReqIdRef.current) return;
          setReverseError(e?.message || "Failed to detect address from map.");
        } finally {
          if (!cancelled && reqId === revReqIdRef.current) setReverseLoading(false);
        }
      })();
    }, 2200);

    return () => {
      if (revDebounceRef.current) clearTimeout(revDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddAddressModal, locTouched, newAddress.location?.lat, newAddress.location?.lng]);

  // ---------------------------
  // Add address to Firestore
  // ---------------------------
  const handleAddAddress = async () => {
    if (!customerUid) return;

    const hasLoc = isValidLatLng(newAddress.location);
    if (!hasLoc) return;

    const willBeDefault = addresses.length === 0 ? true : !!newAddress.isDefault;

    const computedText =
      safeString(newAddress.addressText, "").trim() || latLngText(newAddress.location) || "";

    const label =
      newAddress.type === "Other"
        ? safeString(newAddress.customLabel, "Other").trim() || "Other"
        : safeString(newAddress.type, "Home");

    setSavingAddress(true);

    try {
      const colRef = collection(db, "customers", customerUid, "savedAddresses");

      if (willBeDefault) {
        const batch = writeBatch(db);

        addresses.forEach((a) => {
          if (a.isDefault) {
            const ref = doc(db, "customers", customerUid, "savedAddresses", a.id);
            batch.update(ref, { isDefault: false, updatedAt: serverTimestamp() });
          }
        });

        const newRef = doc(colRef);
        batch.set(
          newRef,
          {
            type: label,
            addressText: computedText,
            phone: safeString(newAddress.phone, customerPhone || "").trim(),
            buildingName: safeString(newAddress.buildingName, "").trim(),
            buildingNo: safeString(newAddress.buildingNo, "").trim(),
            floorNo: safeString(newAddress.floorNo, "").trim(),
            aptNo: safeString(newAddress.aptNo, "").trim(),
            instructions: safeString(newAddress.instructions, "").trim(),
            location: {
              lat: Number(newAddress.location.lat),
              lng: Number(newAddress.location.lng),
            },
            isDefault: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        batch.set(doc(db, "customers", customerUid), { updatedAt: serverTimestamp() }, { merge: true });

        await batch.commit();
        setSelectedAddressId(newRef.id);
      } else {
        const addedRef = await addDoc(colRef, {
          type: label,
          addressText: computedText,
          phone: safeString(newAddress.phone, customerPhone || "").trim(),
          buildingName: safeString(newAddress.buildingName, "").trim(),
          buildingNo: safeString(newAddress.buildingNo, "").trim(),
          floorNo: safeString(newAddress.floorNo, "").trim(),
          aptNo: safeString(newAddress.aptNo, "").trim(),
          instructions: safeString(newAddress.instructions, "").trim(),
          location: {
            lat: Number(newAddress.location.lat),
            lng: Number(newAddress.location.lng),
          },
          isDefault: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        setSelectedAddressId(addedRef.id);

        if (!customerDoc) {
          await setDoc(doc(db, "customers", customerUid), { updatedAt: serverTimestamp() }, { merge: true });
        }
      }

      setShowAddAddressModal(false);
    } catch (err) {
      console.error("Add address error:", err);
    } finally {
      setSavingAddress(false);
    }
  };

  // ✅ Lock background scroll BUT allow modal overlay to scroll
  useEffect(() => {
    if (!showAddAddressModal && !showCityModal) return;

    const html = document.documentElement;
    const body = document.body;

    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPaddingRight = body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - html.clientWidth;
    if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.paddingRight = prevBodyPaddingRight;
    };
  }, [showAddAddressModal, showCityModal]);

  // ---------------------------
  // ✅ Load Taskers from Firestore + join user.status from users/{uid}
  // ✅ City filter uses selectedCity (stable)
  // ---------------------------
  const [taskersFs, setTaskersFs] = useState([]);
  const [taskersLoading, setTaskersLoading] = useState(true);
  const [taskersError, setTaskersError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setTaskersError("");
      setTaskersLoading(true);

      try {
        const colRef = collection(db, "taskers");

        const city = safeString(selectedCity, "").trim();
        const q = city ? query(colRef, where("city", "==", city)) : query(colRef);

        const snap = await getDocs(q);
        const taskersList = snap.docs.map((d) => normalizeTasker({ id: d.id, ...d.data() }));

        const ids = taskersList.map((t) => t.id).filter(Boolean);
        const statusById = new Map();

        const chunkSize = 30;
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);

          const usersSnap = await getDocs(
            query(collection(db, "users"), where(documentId(), "in", chunk))
          );

          usersSnap.docs.forEach((ud) => {
            const data = ud.data() || {};
            statusById.set(ud.id, safeString(data.status, "pending").toLowerCase());
          });
        }

        const merged = taskersList.map((t) => ({
          ...t,
          status: statusById.get(t.id) || "pending",
        }));

        if (!cancelled) {
          setTaskersFs(merged);
          setTaskersLoading(false);
        }
      } catch (err) {
        console.error("taskers load error:", err);
        if (!cancelled) {
          setTaskersFs([]);
          setTaskersLoading(false);
          setTaskersError(err?.message || "Failed to load taskers.");
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedCity]);

  // ---------------------------
  // ✅ Final filtered taskers (LIST ONLY)
  // ---------------------------
  const filteredTaskers = useMemo(() => {
    const q = safeString(searchQuery, "").toLowerCase();
    const center = customerCenter;

    return (taskersFs || []).filter((t) => {
      if (safeString(t.status, "pending").toLowerCase() !== "active") return false;
      
       // ✅ filter out invalid taxonomy 
      if (!Array.isArray(t.categories) || t.categories.length === 0) return false;
      if (!Array.isArray(t.subcategories) || t.subcategories.length === 0) return false;

      const matchesCategory =
        selectedCategory === "all" || (t.categories || []).includes(selectedCategory);

      const name = safeString(t.name, "").toLowerCase();
      const bio = safeString(t.bio, "").toLowerCase();
      const matchesSearch = !q || name.includes(q) || bio.includes(q);

      // radius (based on selected address)
      let matchesRadius = true;

      if (center) {
        const taskerHasLoc = isValidLatLng(t.location);
        const r = safeNumber(t.serviceRadiusKm, null);

        if (!taskerHasLoc || !Number.isFinite(Number(r)) || Number(r) <= 0) {
          matchesRadius = false;
        } else {
          const d = haversineKm(center, t.location);
          matchesRadius = d <= Number(r);
        }
      }

      return matchesCategory && matchesSearch && matchesRadius;
    });
  }, [taskersFs, customerCenter, selectedCategory, searchQuery]);

  const distanceFor = useCallback(
    (t) => {
      if (!customerCenter || !isValidLatLng(t?.location)) return null;
      const d = haversineKm(customerCenter, t.location);
      return Number.isFinite(d) ? d : null;
    },
    [customerCenter]
  );

  const handleTaskerClick = useCallback(
    (taskerId) => {
      if (!taskerId) return;
      navigate(`/customer/plumber/${taskerId}`, {
        state: { selectedAddressId },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 space-y-3">
          {/* ✅ City banner */}
          <div className="w-full bg-green-50 border border-green-100 rounded-xl px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-green-100">
                <MapPin size={18} className="text-[#006C35]" />
              </div>
              <div className="leading-tight">
                <div className="text-xs text-gray-600">Showing taskers in</div>
                <div className="font-semibold text-[#006C35]">
                  {selectedCity ? selectedCity : "Select City"}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowCityModal(true)}
              className="text-sm font-medium text-[#006C35] hover:underline"
            >
              Change City
            </button>
          </div>

          <div className="flex items-center justify-between">
            {/* Address dropdown */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-2 py-1 bg-green-50 rounded-lg text-[#006C35]">
                <MapPin size={20} />
                <select
                  value={selectedAddressId || ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedAddressId(id);
                    localStorage.setItem("selectedAddressId", id); // ✅ persist for refresh
                  }}
                  className="bg-transparent focus:outline-none text-sm font-medium max-w-50 truncate"
                  disabled={addressesLoading || addresses.length === 0}
                >
                  {addressesLoading ? (
                    <option value="">Loading addresses...</option>
                  ) : addresses.length === 0 ? (
                    <option value="">No saved address</option>
                  ) : (
                    addresses
                      .slice()
                      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
                      .map((addr) => (
                        <option key={addr.id} value={addr.id}>
                          {addr.type} - {addr.addressText || "Untitled"}
                          {addr.isDefault ? " (Default)" : ""}
                        </option>
                      ))
                  )}
                </select>
              </div>
            </div>

            {/* Header actions */}
            <div className="flex items-center gap-4">
              <button className="relative" type="button">
                <Bell size={24} className="text-gray-600" />
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  2
                </span>
              </button>

              <button
                onClick={onLogout || logout}
                className="text-gray-600 hover:text-gray-900"
                type="button"
              >
                <LogOut size={24} />
              </button>
            </div>
          </div>

          {/* Add new address button */}
          <button
            onClick={() => setShowAddAddressModal(true)}
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 border border-gray-300 rounded-lg text-gray-500 hover:border-[#006C35] hover:text-[#006C35] transition-colors bg-white"
          >
            <span className="flex items-center gap-2">
              <Plus size={20} />
              Add New Address
            </span>
            <MapPin size={16} />
          </button>
        </div>
      </header>

      {/* ✅ City Modal */}
      {showCityModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 overflow-y-auto overscroll-contain"
          role="dialog"
          aria-modal="true"
          aria-labelledby="select-city-title"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            if (e.target === e.currentTarget) setShowCityModal(false);
          }}
        >
          <div className="min-h-screen w-full px-3 py-6 flex items-start justify-center">
            <div
              className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col max-h-[calc(100dvh-3rem)]"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center">
                    <MapPin size={18} className="text-[#006C35]" />
                  </div>
                  <div>
                    <h3 id="select-city-title" className="font-semibold text-lg text-[#1E3A8A]">
                      Select Your City
                    </h3>
                    <p className="text-xs text-gray-500">Choose a city to find nearby taskers</p>
                  </div>
                </div>

                <button
                  onClick={() => setShowCityModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                  type="button"
                  aria-label="Close"
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto flex-1 min-h-0">
                <div className="grid grid-cols-2 gap-3">
                  {SAUDI_CITIES.map((city) => {
                    const active = safeString(selectedCity, "") === city;
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={async () => {
                          await persistCity(city);
                          setShowCityModal(false);
                        }}
                        className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                          active
                            ? "border-[#006C35] bg-green-50 text-[#006C35]"
                            : "border-gray-200 bg-white hover:bg-gray-50 text-gray-800"
                        }`}
                        disabled={savingCity}
                      >
                        <div className="text-sm font-medium">{city}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="text-xs text-gray-400 text-center mt-4">
                  You can change your city anytime from the header
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Add Address Modal Overlay */}
      {showAddAddressModal && (
        <div
          className="fixed inset-0 z-50 bg-black/50 overflow-y-auto overscroll-contain"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-address-title"
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            if (e.target === e.currentTarget) setShowAddAddressModal(false);
          }}
          onContextMenu={(e) => e.stopPropagation()}
        >
          <div className="min-h-screen w-full px-3 py-4 flex items-start justify-center">
            <div
              className="w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                <h3 id="add-address-title" className="font-semibold text-lg text-[#1E3A8A]">
                  Add New Address
                </h3>
                <button
                  onClick={() => setShowAddAddressModal(false)}
                  className="p-1 hover:bg-gray-100 rounded-full"
                  type="button"
                  aria-label="Close"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0 overscroll-contain">
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setGeoEnabled(true);
                      setGeoStatusNote("Detecting your location...");
                    }}
                    className="h-9"
                  >
                    <LocateFixed size={16} className="mr-2" />
                    Use My Location
                  </Button>
                </div>

                <div className="relative rounded-xl overflow-hidden border border-gray-200">
                  <MapPicker
                    value={isValidLatLng(newAddress.location) ? newAddress.location : DEFAULT_RIYADH}
                    onChange={(coords) => {
                      setLocTouched(true);
                      setNewAddress((p) => ({ ...p, location: coords }));
                    }}
                    height={220}
                    defaultCenter={isValidLatLng(newAddress.location) ? newAddress.location : DEFAULT_RIYADH}
                    defaultZoom={12}
                    userCenter={isValidLatLng(geo?.coords) ? geo.coords : null}
                    userZoom={16}
                    disabled={false}
                    active={true}
                    enableMyLocationButton={true}
                    allowFullscreen={true}
                  />

                  <div className="pointer-events-none absolute left-3 top-3 bg-white/90 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-600 shadow-sm">
                    Tap map or drag the pin to set your location
                  </div>
                </div>

                {/* Label */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                  <div className="flex gap-2">
                    {["Home", "Work", "Other"].map((type) => (
                      <button
                        key={type}
                        onClick={() =>
                          setNewAddress((p) => ({
                            ...p,
                            type,
                            customLabel: type === "Other" ? p.customLabel : "",
                          }))
                        }
                        type="button"
                        className={`flex-1 py-2 text-sm rounded-lg border ${
                          newAddress.type === type
                            ? "bg-[#006C35] text-white border-[#006C35]"
                            : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>

                  {newAddress.type === "Other" ? (
                    <div className="mt-3">
                      <input
                        type="text"
                        value={safeString(newAddress.customLabel, "")}
                        onChange={(e) =>
                          setNewAddress((p) => ({
                            ...p,
                            customLabel: e.target.value,
                          }))
                        }
                        placeholder='Custom label (e.g. "Parents", "Office 2")'
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                      />
                    </div>
                  ) : null}
                </div>

                {/* Address (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address{" "}
                    {reverseLoading ? (
                      <span className="ml-2 text-xs text-gray-500">(detecting...)</span>
                    ) : null}
                  </label>

                  <div className="border border-gray-300 rounded-lg px-4 py-3 bg-gray-50">
                    <div className="text-sm text-gray-800">
                      {safeString(newAddress.addressText, "").trim()
                        ? newAddress.addressText
                        : isValidLatLng(newAddress.location)
                        ? "No address found here — move the pin slightly."
                        : "Move the pin to select your location."}
                    </div>

                    {isValidLatLng(newAddress.location) ? (
                      <div className="text-xs text-gray-500 mt-1">{latLngText(newAddress.location)}</div>
                    ) : null}
                  </div>

                  {reverseError ? <p className="text-xs text-red-600 mt-1">{reverseError}</p> : null}
                  {geoStatusNote ? <p className="text-xs text-gray-500 mt-1">{geoStatusNote}</p> : null}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={safeString(newAddress.phone, "")}
                    onChange={(e) => setNewAddress((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="+966 55xxxxxxx"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                  />
                </div>

                {/* Building details */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Building Name</label>
                    <input
                      type="text"
                      value={safeString(newAddress.buildingName, "")}
                      onChange={(e) => setNewAddress((p) => ({ ...p, buildingName: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Building No</label>
                    <input
                      type="text"
                      value={safeString(newAddress.buildingNo, "")}
                      onChange={(e) => setNewAddress((p) => ({ ...p, buildingNo: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Floor No</label>
                    <input
                      type="text"
                      value={safeString(newAddress.floorNo, "")}
                      onChange={(e) => setNewAddress((p) => ({ ...p, floorNo: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apt No</label>
                    <input
                      type="text"
                      value={safeString(newAddress.aptNo, "")}
                      onChange={(e) => setNewAddress((p) => ({ ...p, aptNo: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                  <textarea
                    rows={3}
                    value={safeString(newAddress.instructions, "")}
                    onChange={(e) => setNewAddress((p) => ({ ...p, instructions: e.target.value }))}
                    placeholder="Gate code, landmark, call on arrival..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                  />
                </div>

                {/* Default */}
                <div className="flex items-center gap-2">
                  <input
                    id="makeDefault"
                    type="checkbox"
                    checked={!!newAddress.isDefault}
                    onChange={(e) => setNewAddress((p) => ({ ...p, isDefault: e.target.checked }))}
                  />
                  <label htmlFor="makeDefault" className="text-sm text-gray-600">
                    Set as default
                  </label>
                </div>

                <Button
                  onClick={handleAddAddress}
                  className="w-full bg-[#006C35] hover:bg-[#005428] py-6 text-base"
                  disabled={!customerUid || savingAddress || !isValidLatLng(newAddress.location)}
                  type="button"
                >
                  {savingAddress ? "Saving..." : "Confirm Address"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Profile */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#006C35]/10 flex items-center justify-center text-[#006C35] text-xl font-semibold">
              {customerName ? customerName.charAt(0).toUpperCase() : "?"}
            </div>

            <div>
              <h3 className="text-[#1E3A8A] leading-tight">{customerName}</h3>

              <p className="text-sm text-gray-500">
                {selectedCity ? `${selectedCity} , Saudi Arabia` : "Saudi Arabia"}
              </p>

              <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                <Star size={16} className="text-yellow-500 fill-yellow-500" />
                <span>{customerRating}</span>
                <span>•</span>
                <span>{`${requestedServices} Request`}</span>
                <span>•</span>
                <span>{`${completedJobs} Job Completed`}</span>
              </div>
            </div>
          </div>

          <Link
            to={`/customer/edit-profile`}
            onClick={() => setShowAddAddressModal(false)}
            className="inline-block mt-2 text-[#006C35]"
          >
            View Profile
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Quick Actions */}
        <div className="mb-6">
          <Link
            to="/customer/my-requests"
            onClick={() => setShowAddAddressModal(false)}
            className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <List size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm">My Service Requests</h3>
                <p className="text-xs text-gray-500">View and manage your posted requests</p>
              </div>
            </div>
            <span className="text-gray-400">→</span>
          </Link>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="search">Search Taskers</TabsTrigger>
            <TabsTrigger value="post">Post Request</TabsTrigger>
          </TabsList>

          {/* Search Taskers Tab */}
          <TabsContent value="search">
            {/* Category Filter */}
            <div className="mb-6 overflow-x-auto">
              <div className="flex gap-2 pb-2">
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === "all"
                      ? "bg-[#006C35] text-white"
                      : "bg-white text-gray-700 border border-gray-300"
                  }`}
                >
                  All Services
                </button>

                {categoriesLoading ? <div className="text-sm text-gray-500 px-2">Loading services...</div> : null}
                {!categoriesLoading && categoriesError ? (
                  <div className="text-sm text-red-600 px-2">{categoriesError}</div>
                ) : null}

                {(categories || []).map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                      selectedCategory === category.id
                        ? "bg-[#006C35] text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    {category.icon} {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Search input */}
            <div className="mb-6">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or bio..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006C35] bg-white"
              />
            </div>

            {/* Load status */}
            {taskersLoading ? (
              <div className="bg-white rounded-lg p-6 text-sm text-gray-500">Loading taskers...</div>
            ) : taskersError ? (
              <div className="bg-white rounded-lg p-6 text-sm text-red-600">{taskersError}</div>
            ) : null}

            {/* LIST ONLY */}
            <div className="space-y-4">
              {filteredTaskers.length === 0 ? (
                <div className="bg-white rounded-lg p-8 text-center">
                  <p className="text-gray-500">No taskers found matching your criteria</p>
                  {customerCenter ? (
                    <p className="text-xs text-gray-400 mt-2">
                      This can happen if no active taskers cover your location within their radius.
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-2">Select an address to filter by radius.</p>
                  )}
                </div>
              ) : (
                filteredTaskers.map((t) => {
                  const d = distanceFor(t);
                  return (
                    <div key={t.id} className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex gap-4">
                        <img
                          src={t.imageUrl || "/placeholder-avatar.png"}
                          alt={t.name}
                          className="w-20 h-20 rounded-lg object-cover bg-gray-100"
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder-avatar.png";
                          }}
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-[#1E3A8A] mb-1">{t.name || "Tasker"}</h3>
                              <div className="flex items-center gap-3 text-sm text-gray-600">
                                <div className="flex items-center gap-1">
                                  <Star size={16} className="text-yellow-500 fill-yellow-500" />
                                  <span>{Number(t?.ratingSummary?.avg || 0).toFixed(1)}</span>
                                  <span className="text-gray-400">({Number(t?.ratingSummary?.count || 0)})</span>
                                </div>

                                {d !== null ? (
                                  <div className="flex items-center gap-1">
                                    <MapPin size={16} />
                                    <span>{d.toFixed(1)} km</span>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <p className="text-sm text-gray-600 mb-3">{t.bio}</p>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {(t.categories || []).map((catId) => {
                              const category = (categories || []).find((c) => c.id === catId);
                              return category ? (
                                <span key={catId} className="px-2 py-1 bg-green-50 text-[#006C35] text-sm rounded">
                                  {category.icon} {category.name}
                                </span>
                              ) : null;
                            })}
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              Available: <span className="text-[#006C35]">{t.availability || "—"}</span>
                            </span>

                            <button
                              type="button"
                              onClick={() => handleTaskerClick(t.id)}
                              className="px-4 py-2 bg-[#006C35] text-white rounded-lg hover:bg-[#005428] transition-colors"
                            >
                              View Profile
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Post Request Tab */}
          <TabsContent value="post">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h2 className="text-[#1E3A8A] mb-6">Post a Service Request</h2>

              <PostServiceRequestForm
                categories={categories || []}
                selectedAddress={selectedAddress}
                customer={{ id: customerUid, name: customerName }}
              />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// -----------------------------
// Post a Service Request (validated)
// -----------------------------
function PostServiceRequestForm({ categories, selectedAddress, customer }) {
  const navigate = useNavigate();

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");

  const [formData, setFormData] = useState({
    categoryId: "",
    subcategoryId: "",
    title: "",
    description: "",
    locationText: safeString(selectedAddress?.addressText, "Riyadh"),
    budget: "",
    preferredDate: "",
    preferredTime: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [toast, setToast] = useState({ open: false, message: "", type: "success" });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      locationText: safeString(selectedAddress?.addressText, "Riyadh"),
    }));
  }, [selectedAddress?.id, selectedAddress?.addressText]);

  useEffect(() => {
    if (!toast.open) return;
    const t = setTimeout(() => setToast((p) => ({ ...p, open: false })), 2200);
    return () => clearTimeout(t);
  }, [toast.open]);

  const category = (categories || []).find((c) => c.id === selectedCategory);

  const validate = () => {
    const next = {};

    if (!safeString(formData.categoryId).trim()) next.categoryId = "this category mandatory.";
    if (!safeString(formData.subcategoryId).trim()) next.subcategoryId = "this subcategory mandatory.";
    if (!safeString(formData.title).trim()) next.title = "this title mandatory.";
    if (!safeString(formData.locationText).trim()) next.locationText = "this location mandatory.";

    if (formData.budget === "" || formData.budget === null || Number(formData.budget) <= 0)
      next.budget = "this budget mandatory.";

    if (!safeString(formData.preferredDate).trim()) next.preferredDate = "this preferred date mandatory.";
    if (!safeString(formData.preferredTime).trim()) next.preferredTime = "this preferred time mandatory.";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const inputBase =
    "w-full px-4 py-3 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#006C35]";
  const inputError = "border-red-500 focus:ring-red-200";
  const inputOk = "border-gray-300";

  const onSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;
    if (!validate()) return;

    setSubmitting(true);

    try {
      // eslint-disable-next-line no-unused-vars
      const requestId = await submitServiceRequest(formData, selectedAddress, customer);

      setToast({
        open: true,
        message: "Request was successfully submitted",
        type: "success",
      });

      navigate(`/customer/home`);
    } catch (err) {
      console.error(err);

      setToast({
        open: true,
        message: "Failed to submit request. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Toast
        open={toast.open}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      />

      <form className="space-y-5" onSubmit={onSubmit}>
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Service Category
          </label>

          <select
            value={formData.categoryId}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedCategory(v);
              setSelectedSubcategory("");

              setFormData((prev) => ({
                ...prev,
                categoryId: v,
                subcategoryId: "",
              }));

              clearError("categoryId");
              clearError("subcategoryId");
            }}
            className={`${inputBase} ${errors.categoryId ? inputError : inputOk}`}
          >
            <option value="">Select a category...</option>

            {(categories || []).map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>

          {errors.categoryId && (
            <p className="text-xs text-red-600 mt-1">{errors.categoryId}</p>
          )}
        </div>

        {/* Subcategory */}
        {selectedCategory && category ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specific Service
            </label>

            <select
              value={formData.subcategoryId}
              onChange={(e) => {
                const v = e.target.value;

                setSelectedSubcategory(v);

                setFormData((prev) => ({
                  ...prev,
                  subcategoryId: v,
                }));

                clearError("subcategoryId");
              }}
              className={`${inputBase} ${errors.subcategoryId ? inputError : inputOk}`}
            >
              <option value="">Select a service...</option>

              {(category.subcategories || []).map((subcat) => (
                <option key={subcat.id} value={subcat.id}>
                  {subcat.name}
                </option>
              ))}
            </select>

            {errors.subcategoryId ? (
              <p className="text-xs text-red-600 mt-1">{errors.subcategoryId}</p>
            ) : selectedSubcategory ? (
              <p className="text-sm text-gray-500 mt-2">
                {
                  category.subcategories?.find((s) => s.id === selectedSubcategory)
                    ?.description
                }
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Request Title
          </label>

          <input
            type="text"
            value={safeString(formData.title, "")}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                title: e.target.value,
              }));
              clearError("title");
            }}
            placeholder="e.g., Fix leaking kitchen sink"
            className={`${inputBase} ${errors.title ? inputError : inputOk}`}
          />

          {errors.title && (
            <p className="text-xs text-red-600 mt-1">{errors.title}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (Optional)
          </label>

          <textarea
            value={safeString(formData.description, "")}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            rows={4}
            placeholder="Describe your service needs in detail..."
            className={`${inputBase} ${inputOk}`}
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location
          </label>

          <input
            type="text"
            value={safeString(formData.locationText, "")}
            readOnly
            className={`w-full px-4 py-3 border rounded-lg bg-gray-100 focus:outline-none ${
              errors.locationText ? "border-red-500" : "border-gray-300"
            }`}
          />

          {errors.locationText ? (
            <p className="text-xs text-red-600 mt-1">{errors.locationText}</p>
          ) : selectedAddress?.id ? (
            <p className="text-xs text-gray-500 mt-2">
              Using selected address:{" "}
              <span className="font-medium">
                {safeString(selectedAddress.type, "Address")}
              </span>
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-2">
              No saved address yet. Add one to autofill location.
            </p>
          )}
        </div>

        {/* Budget */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Budget (SAR)
          </label>

          <input
            type="number"
            value={safeString(formData.budget, "")}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                budget: e.target.value,
              }));
              clearError("budget");
            }}
            placeholder="Your budget"
            className={`${inputBase} ${errors.budget ? inputError : inputOk}`}
          />

          {errors.budget && (
            <p className="text-xs text-red-600 mt-1">{errors.budget}</p>
          )}
        </div>

        {/* Date & Time */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Date
            </label>

            <input
              type="date"
              value={safeString(formData.preferredDate, "")}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  preferredDate: e.target.value,
                }));
                clearError("preferredDate");
              }}
              className={`${inputBase} ${errors.preferredDate ? inputError : inputOk}`}
            />

            {errors.preferredDate && (
              <p className="text-xs text-red-600 mt-1">
                {errors.preferredDate}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Time
            </label>

            <input
              type="time"
              value={safeString(formData.preferredTime, "")}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  preferredTime: e.target.value,
                }));
                clearError("preferredTime");
              }}
              className={`${inputBase} ${errors.preferredTime ? inputError : inputOk}`}
            />

            {errors.preferredTime && (
              <p className="text-xs text-red-600 mt-1">
                {errors.preferredTime}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#006C35] hover:bg-[#005428] text-white py-6 disabled:opacity-60"
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Post Service Request"}
        </Button>

        <p className="text-sm text-gray-500 text-center">
          Your request will be visible to nearby taskers who can then send you offers.
        </p>
      </form>
    </>
  );
}

// -----------------------------
// Writes to: serviceRequests
// -----------------------------
async function submitServiceRequest(formData, selectedAddress, customer) {
  const user = auth.currentUser;
  if (!user) throw new Error("Not authenticated");

  const hasSelected = !!selectedAddress?.id;

  const loc =
    hasSelected && typeof selectedAddress?.location === "object"
      ? {
          lat: Number(selectedAddress.location?.lat) || null,
          lng: Number(selectedAddress.location?.lng) || null,
        }
      : { lat: null, lng: null };

  const docRef = await addDoc(collection(db, "serviceRequests"), {
    customerId: user.uid,

    addressId: hasSelected ? selectedAddress.id : null,

    categoryId: safeString(formData.categoryId, ""),
    subcategoryId: safeString(formData.subcategoryId, ""),

    title: safeString(formData.title, ""),
    description: safeString(formData.description, ""),

    locationText: safeString(
      hasSelected ? selectedAddress.addressText : formData.locationText,
      "Riyadh"
    ),
    location: loc,

    budget: Number(formData.budget) || 0,
    preferredDate: safeString(formData.preferredDate, ""),
    preferredTime: safeString(formData.preferredTime, ""),

    status: "open",
    taskerId: null,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}


function Toast({ open, message, type = "success", onClose }) {
  if (!open) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-60 px-4 w-full max-w-md">
      <div
        className={`w-full rounded-xl border shadow-lg px-4 py-3 flex items-start gap-3 bg-white ${
          type === "success" ? "border-emerald-200" : "border-red-200"
        }`}
        role="status"
        aria-live="polite"
      >
        <div className={`mt-0.5 w-2.5 h-2.5 rounded-full ${type === "success" ? "bg-emerald-500" : "bg-red-500"}`} />
        <div className="flex-1">
          <p className="text-sm text-gray-900">{message}</p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}